import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { useTabScope } from '../../contexts/TabScopeContext';
import { tabScopedStore } from './tabScopedStore';

export type DomainTreeFilterState = {
  selectedNodes: string[];
};

const DOMAIN_TREE_DEFAULTS: DomainTreeFilterState = {
  selectedNodes: [],
};

const SUB_TAB_SCOPE_SUFFIX_RE = /\/subTab_[^/]+$/;

function isDomainTreeFilterState(
  value: unknown,
): value is DomainTreeFilterState {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { selectedNodes?: unknown };
  return (
    Array.isArray(candidate.selectedNodes) &&
    candidate.selectedNodes.every(
      (selectedNode): selectedNode is string =>
        typeof selectedNode === 'string',
    )
  );
}

export function normalizeDomainTreeScopeKey(
  scopeKey?: string,
): string | undefined {
  if (!scopeKey) return undefined;
  return scopeKey.replace(SUB_TAB_SCOPE_SUFFIX_RE, '');
}

export function useTabScopedDomainTree() {
  const scopeKey = useTabScope();
  const normalizedScopeKey = normalizeDomainTreeScopeKey(scopeKey);

  const scopedEntry = useStore(tabScopedStore, (state) => {
    if (!normalizedScopeKey) return undefined;
    const entry = state.entries[normalizedScopeKey];
    return isDomainTreeFilterState(entry) ? entry : undefined;
  });
  const updateEntry = useStore(tabScopedStore, (state) => state.updateEntry);

  const selectedNodes = useMemo(
    () => scopedEntry?.selectedNodes ?? DOMAIN_TREE_DEFAULTS.selectedNodes,
    [scopedEntry],
  );

  const setSelectedNodes = useCallback(
    (nodes: string[]) => {
      if (!normalizedScopeKey) return;
      updateEntry(normalizedScopeKey, { selectedNodes: nodes });
    },
    [normalizedScopeKey, updateEntry],
  );

  const clearDomainTreeSelection = useCallback(
    () => setSelectedNodes([]),
    [setSelectedNodes],
  );

  return {
    selectedNodes,
    setSelectedNodes,
    clearDomainTreeSelection,
  };
}
